import SEO from '../components/SEO';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Star, Check, Truck, RefreshCw, Leaf, BookOpen, ArrowRight } from 'lucide-react';
import { PRODUCT_IMAGES, PRODUCT_PRICE, BUNDLE_2_PRICE } from '../constants';
import { useCart } from '../context/CartContext';
import { trackViewContent } from '../utils/metaPixel';

const bullets = [
  'Supports semen volume naturally',
  'Supports healthy libido & drive',
  'Premium botanical extracts',
  '60 capsules per bottle',
  'Just 2 capsules daily',
  'No artificial additives',
];

const trustItems = [
  { icon: Truck, label: 'Free UK Delivery on 2+ bottles' },
  { icon: RefreshCw, label: '30-Day Money-Back Guarantee' },
  { icon: Leaf, label: 'Premium Plant-Based Formula' },
];

const purchaseOptions = [
  {
    id: 'one-time',
    label: 'One-Time Purchase',
    price: `£${PRODUCT_PRICE.toFixed(2)}`,
    priceNote: null,
    sub: 'Delivery calculated at checkout',
    badge: null,
    recommended: false,
  },
  {
    id: 'buy-two',
    label: 'Buy 2 Bottles',
    price: `£${BUNDLE_2_PRICE.toFixed(2)}`,
    priceNote: null,
    sub: 'Free UK Delivery included',
    badge: 'Best Value',
    recommended: true,
  },
];

function PurchaseOption({ option, selected, onSelect }) {
  const isActive = selected === option.id;
  const isRecommended = option.recommended;

  return (
    <button
      data-testid={`purchase-option-${option.id}`}
      onClick={() => onSelect(option.id)}
      style={{
        width: '100%',
        textAlign: 'left',
        padding: isRecommended ? '18px 20px' : '15px 20px',
        border: `2px solid ${isActive ? '#1B3D7E' : '#E2E8F0'}`,
        borderRadius: '14px',
        backgroundColor: isActive && isRecommended
          ? 'rgba(27,61,126,0.05)'
          : isActive
          ? 'rgba(27,61,126,0.03)'
          : '#FFFFFF',
        cursor: 'pointer',
        transition: 'border-color 0.2s, background-color 0.2s',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
      }}
    >
      {/* Badge */}
      {option.badge && (
        <span style={{
          position: 'absolute',
          top: '-9px',
          right: '14px',
          backgroundColor: isRecommended ? '#1B3D7E' : '#64748B',
          color: '#FFFFFF',
          fontFamily: 'Manrope, sans-serif',
          fontSize: '0.6rem',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          padding: '2px 10px',
          borderRadius: '100px',
          whiteSpace: 'nowrap',
        }}>
          {isRecommended && '★ '}{option.badge}
        </span>
      )}

      {/* Radio circle */}
      <div style={{
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        border: `2px solid ${isActive ? '#1B3D7E' : '#CBD5E1'}`,
        backgroundColor: isActive ? '#1B3D7E' : 'transparent',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
      }}>
        {isActive && <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#FFFFFF' }} />}
      </div>

      {/* Label + sub */}
      <div style={{ flex: 1 }}>
        <span style={{
          fontFamily: 'Outfit, sans-serif',
          fontSize: '0.92rem',
          fontWeight: 700,
          color: isActive ? '#1B3D7E' : '#0D1A2E',
          letterSpacing: '-0.01em',
          display: 'block',
          marginBottom: '2px',
          transition: 'color 0.2s',
        }}>
          {option.label}
        </span>
        <span style={{
          fontFamily: 'Manrope, sans-serif',
          fontSize: '0.72rem',
          color: '#94A3B8',
          fontWeight: 500,
        }}>
          {option.sub}
        </span>
      </div>

      {/* Price */}
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <span style={{
          fontFamily: 'Outfit, sans-serif',
          fontSize: '1.05rem',
          fontWeight: 800,
          color: isActive ? '#1B3D7E' : '#0D1A2E',
          transition: 'color 0.2s',
        }}>
          {option.price}
        </span>
        {option.priceNote && (
          <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.7rem', color: '#94A3B8', fontWeight: 500 }}>
            {option.priceNote}
          </span>
        )}
      </div>
    </button>
  );
}

export default function Shop() {
  const [selected, setSelected] = useState('one-time');
  const { addToCart } = useCart();

  // Fire ViewContent once when the product page is viewed (consent-gated inside trackViewContent)
  useEffect(() => {
    trackViewContent(PRODUCT_PRICE);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCTA = () => {
    addToCart(selected === 'buy-two' ? 2 : 1, PRODUCT_IMAGES.twoBottles);
  };

  const ctaLabel = selected === 'buy-two'
    ? `Add to Basket — £${BUNDLE_2_PRICE.toFixed(2)}`
    : `Add to Basket — £${PRODUCT_PRICE.toFixed(2)}`;

  const CtaIcon = ShoppingBag;

  return (
    <main data-testid="shop-page" style={{ backgroundColor: '#F7F7F5', minHeight: '100vh', paddingTop: '40px', paddingBottom: '80px' }}>
      <SEO
        title="Buy RELOAD | Increase Semen Volume Supplement | £24.99 UK"
        description="Buy RELOAD Male Performance Formula. Designed to increase semen volume, help you ejaculate more, and boost male reproductive health. £24.99. Free UK delivery on 2 bottles."
        canonical="/shop"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Breadcrumb */}
        <nav style={{ marginBottom: '36px' }}>
          <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.75rem', color: '#94A3B8', letterSpacing: '0.05em' }}>
            Home &nbsp;/&nbsp; <span style={{ color: '#1B3D7E', fontWeight: 600 }}>Shop</span>
          </span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Product image */}
          <div data-testid="shop-product-image-col" style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '24px',
            padding: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'sticky',
            top: '100px',
          }}>
            <div style={{ position: 'relative', width: '100%', maxWidth: '380px' }}>
              <div style={{
                position: 'absolute',
                inset: '-20px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(27,61,126,0.06) 0%, transparent 70%)',
              }} />
              <img
                src={PRODUCT_IMAGES.twoBottles}
                alt="RELOAD Male Performance Formula"
                data-testid="shop-product-image"
                style={{
                  width: '100%',
                  objectFit: 'contain',
                  position: 'relative',
                  zIndex: 1,
                  filter: 'drop-shadow(0 20px 40px rgba(13,26,46,0.12))',
                }}
              />
            </div>
          </div>

          {/* Right: Product info + purchase */}
          <div data-testid="shop-product-info">
            <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#1B3D7E', fontWeight: 700 }}>RELOAD Nutrition</span>

            <h1 style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
              color: '#0D1A2E',
              letterSpacing: '-0.025em',
              lineHeight: 1.1,
              marginTop: '8px',
              marginBottom: '14px',
            }}>
              Male Performance<br />Formula
            </h1>

            {/* Stars */}
            <div className="flex items-center gap-2" style={{ marginBottom: '20px' }}>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} size={13} fill="#1B3D7E" stroke="#1B3D7E" />)}
              </div>
              <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.78rem', color: '#64748B', fontWeight: 600 }}>4.9 / 5 &nbsp;·&nbsp; 2,000+ reviews</span>
            </div>

            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.88rem', color: '#64748B', lineHeight: 1.8, marginBottom: '22px' }}>
              RELOAD is a premium botanical formula designed to support male reproductive health — volume, libido, and vitality — using only natural, plant-based ingredients.
            </p>

            {/* Bullets */}
            <ul style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '28px', listStyle: 'none' }}>
              {bullets.map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: 'rgba(27,61,126,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Check size={10} style={{ color: '#1B3D7E' }} strokeWidth={2.5} />
                  </div>
                  <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.85rem', color: '#0D1A2E', fontWeight: 500 }}>{item}</span>
                </li>
              ))}
            </ul>

            <div style={{ height: '1px', backgroundColor: '#F1F5F9', marginBottom: '26px' }} />

            {/* Purchase Options */}
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.72rem', fontWeight: 700, color: '#64748B', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '14px' }}>
              Choose your plan
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {purchaseOptions.map((option) => (
                <PurchaseOption
                  key={option.id}
                  option={option}
                  selected={selected}
                  onSelect={setSelected}
                />
              ))}
            </div>

            {/* CTA Button */}
            <button
              data-testid="shop-add-to-basket"
              onClick={handleCTA}
              style={{
                width: '100%',
                padding: '17px',
                backgroundColor: '#1B3D7E',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '14px',
                fontFamily: 'Manrope, sans-serif',
                fontSize: '0.95rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                marginBottom: '12px',
                transition: 'background-color 0.2s, transform 0.1s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#152e61'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1B3D7E'; e.currentTarget.style.transform = 'none'; }}
            >
              <CtaIcon size={17} strokeWidth={2} />
              {ctaLabel}
            </button>

            {/* Trust indicators */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '9px', padding: '16px', backgroundColor: '#F7F7F5', borderRadius: '12px' }}>
              {trustItems.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <Icon size={13} style={{ color: '#1B3D7E', flexShrink: 0 }} strokeWidth={1.5} />
                  <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.78rem', color: '#64748B', fontWeight: 600 }}>{label}</span>
                </div>
              ))}
            </div>

            {/* Blog links strip */}
            <div
              data-testid="shop-blog-links"
              style={{
                marginTop: '24px',
                padding: '18px 20px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #E2E8F0',
                borderRadius: '12px',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '12px' }}>
                <BookOpen size={13} style={{ color: '#1B3D7E', flexShrink: 0 }} strokeWidth={1.5} />
                <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.65rem', fontWeight: 700, color: '#1B3D7E', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                  Learn More
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { label: 'How to increase semen volume naturally', to: '/blog/how-to-increase-semen-volume-naturally' },
                  { label: 'Best supplements for male reproductive health', to: '/blog/best-supplements-male-reproductive-health' },
                  { label: 'Low libido in men: causes & natural solutions', to: '/blog/low-libido-men-causes-natural-solutions' },
                ].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    data-testid={`shop-blog-link-${link.to.split('/').pop()}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontFamily: 'Manrope, sans-serif',
                      fontSize: '0.8rem',
                      color: '#475569',
                      textDecoration: 'none',
                      fontWeight: 500,
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#1B3D7E'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#475569'}>
                    <ArrowRight size={11} style={{ color: '#1B3D7E', flexShrink: 0 }} />
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
